import { useState } from 'react';
import { useCustom, useMany } from '@refinedev/core';
import { Card, Flex, Select, Table, Tag } from 'antd';
import type {
  PublicationListOut,
  PublicationStatsOut,
} from '@/api/generated/schemas';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { formatDuration } from '@/utils/duration';
import { StatsRangePicker } from './StatsRangePicker';
import {
  DEFAULT_DAYS,
  daysBack,
  rangeQuery,
  type StatsRange,
} from './statsRange';

const VARIANT_COLORS: Record<string, string> = {
  full: 'green',
  preview: 'blue',
  demo: 'gold',
};

type RankTab = 'listening_time' | 'readers';

// `key` is the endpoint suffix — each tab is its own ranking.
const TABS: { key: RankTab; label: string }[] = [
  { key: 'listening_time', label: 'Czas odsłuchu' },
  { key: 'readers', label: 'Odbiorcy' },
];

const LIMIT_OPTIONS = [5, 10, 25, 50, 100].map((n) => ({
  label: `Top ${n}`,
  value: n,
}));

export const TopPublicationsTable = () => {
  const [tab, setTab] = useState<RankTab>('listening_time');
  const [limit, setLimit] = useState(10);
  const [range, setRange] = useState<StatsRange>(() => daysBack(DEFAULT_DAYS));

  const { query } = useCustom<PublicationStatsOut[]>({
    url: `progress/sessions/stats/top_publications/${tab}`,
    method: 'get',
    config: { query: { limit, ...rangeQuery(range) } },
  });

  const data = (query.data?.data ?? []) as PublicationStatsOut[];

  const publicationIds = data.map((r) => r.publication_id).filter(Boolean);

  const { result: publicationsResult } = useMany<PublicationListOut>({
    resource: 'user_catalog.publications',
    ids: publicationIds,
    queryOptions: { enabled: publicationIds.length > 0 },
  });

  const publicationMap = new Map<number, PublicationListOut>(
    (publicationsResult?.data ?? []).map((p: PublicationListOut) => [p.id, p])
  );

  return (
    <Card
      title="TOP publikacje"
      tabList={TABS}
      activeTabKey={tab}
      onTabChange={(key) => setTab(key as RankTab)}
      extra={
        <Flex gap="small" wrap>
          <Select<number>
            options={LIMIT_OPTIONS}
            value={limit}
            onChange={setLimit}
            style={{ width: 110 }}
          />
          <StatsRangePicker value={range} onChange={setRange} />
        </Flex>
      }
    >
      <Table
        dataSource={data}
        rowKey={(r) => `${r.product_id}-${r.product_variant}`}
        loading={query.isFetching}
        pagination={false}
        size="small"
      >
        <Table.Column
          title="#"
          render={(_val: unknown, _rec: unknown, index: number) => index + 1}
          width={40}
        />
        <Table.Column
          dataIndex="publication_id"
          title="Publikacja"
          render={(id: number) => (
            <PublicationInfoPopover
              publication={publicationMap.get(id)}
              publicationId={id}
            />
          )}
        />
        <Table.Column
          dataIndex="product_kind"
          title="Rodzaj"
          render={(v: string) => (
            <Tag color={VARIANT_COLORS[v] ?? 'default'}>{v}</Tag>
          )}
        />
        <Table.Column
          dataIndex="product_variant"
          title="Wariant"
          render={(v: string) => (
            <Tag color={VARIANT_COLORS[v] ?? 'default'}>{v}</Tag>
          )}
        />
        <Table.Column
          dataIndex="covered_seconds"
          title="Czas odsłuchu"
          render={(s: number) => formatDuration(s)}
        />
        <Table.Column dataIndex="unique_readers" title="Czytelnicy" />
      </Table>
    </Card>
  );
};
